/**
 * Walkthrough bundle schema — the envelope shape pushed to
 * `POST /api/projects/:id/walkthroughs/push` in the walkthrough-finding-
 * cloud-mirror change.
 *
 * A bundle is the atomic unit of a completed local walkthrough: the
 * manifest (one row) + steps (many) + inlined findings (many) +
 * artifact references (many). Screenshot bytes travel as binary
 * multipart parts keyed by `part_name`; this schema only describes the
 * JSON envelope. The bundle builder in `packages/local-store` attaches
 * the binary parts; the cloud handler streams them to disk after
 * validating this envelope.
 *
 * Same Zod schema validates both ends: the client builder constructs
 * from it, the cloud handler parses against it. Drift failures become
 * compile errors.
 *
 * See:
 *  - `openspec/changes/walkthrough-finding-cloud-mirror/proposal.md`
 *  - `openspec/changes/walkthrough-finding-cloud-mirror/specs/walkthrough-cloud-mirror/spec.md`
 */
import { z } from "zod";
import { isoTimestamp } from "./frontmatter/common.js";
import { findingFrontmatterSchema } from "./frontmatter/finding.js";

/**
 * Allowed MIME types for screenshot artifacts. Restricting at the
 * schema layer keeps the cloud handler's on-disk extension mapping
 * trivial (`image/png` → `.png`, etc.) and makes the attack surface
 * explicit — new formats land via deliberate schema extension, not
 * by dropping a new content-type header on a POST.
 */
export const SCREENSHOT_CONTENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
] as const;
export type ScreenshotContentType = (typeof SCREENSHOT_CONTENT_TYPES)[number];

const partNameSchema = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[a-z][a-z0-9_-]*$/, {
    message:
      "part_name must be kebab-or-snake-case ASCII starting with a letter",
  })
  .describe(
    "Key of the matching multipart form field in the bundle body. Must match a binary part exactly.",
  );

/**
 * An artifact-reference entry in the manifest. Every binary part the
 * multipart body carries gets one of these — the cloud handler uses
 * `part_name` to match, `kind` to route (screenshots stream to the
 * per-run directory; other kinds extend later without schema churn),
 * and `bytes_hash` to verify integrity when present.
 */
export const artifactRefSchema = z.object({
  kind: z
    .enum(["screenshot", "report", "other"])
    .describe(
      "Artifact family. 'screenshot' streams to files/walkthroughs/<run_id>/step-<seq>.<ext>; 'report' / 'other' reserved for future kinds.",
    ),
  part_name: partNameSchema,
  filename: z
    .string()
    .min(1)
    .max(256)
    .describe(
      "Original filename as the client saw it. Used for logging and as a fallback display label.",
    ),
  content_type: z
    .string()
    .min(1)
    .max(128)
    .describe(
      "MIME type. Screenshots MUST use one of image/png | image/jpeg | image/webp — the cloud handler enforces that subset separately.",
    ),
  bytes_hash: z
    .string()
    .length(64)
    .regex(/^[0-9a-f]+$/)
    .optional()
    .describe(
      "Lowercase hex sha256 of the binary bytes. Optional in v1; client populates when available so cloud can detect transport corruption.",
    ),
});
export type ArtifactRef = z.infer<typeof artifactRefSchema>;

/**
 * Reference from a step record back into `artifacts[]`. Keeps step
 * records self-describing — readers see "step has a screenshot at this
 * part_name" without joining across arrays.
 */
export const stepScreenshotRefSchema = z.object({
  part_name: partNameSchema,
  filename: z.string().min(1).max(256),
  content_type: z.string().min(1).max(128),
});
export type StepScreenshotRef = z.infer<typeof stepScreenshotRefSchema>;

/**
 * One recorded step. Mirrors the shape of `record_step` inputs + local
 * `steps.jsonl` entries, minus the host-resolved screenshot bytes.
 */
export const bundleStepSchema = z.object({
  seq: z
    .number()
    .int()
    .min(1)
    .describe(
      "1-based step sequence. Persisted to `walkthrough_steps.bundle_order` so render order survives DB insert-order drift.",
    ),
  title: z
    .string()
    .min(1)
    .max(2048)
    .describe(
      "Short first-person heading for the step — shows in the rendered report.",
    ),
  persona_reasoning: z
    .string()
    .max(65_536)
    .nullable()
    .optional()
    .describe(
      "First-person inner voice capture. Nullable for terse / action-only steps.",
    ),
  grounding: z
    .string()
    .max(65_536)
    .nullable()
    .optional()
    .describe(
      "Optional citations, domain-knowledge grounding, or verification notes for this step.",
    ),
  created_at: isoTimestamp.describe(
    "Client-side ISO timestamp. Trusted as-is by the cloud (runner writes cloud-side already trust the runner's clock — same invariant).",
  ),
  fingerprint: z
    .unknown()
    .nullable()
    .optional()
    .describe(
      "Full StepFingerprint object (dom/ax/screenshot hashes + network + console signals). Passed through verbatim to walkthrough_steps.fingerprint; runtime shape validated by the fingerprint package on write.",
    ),
  screenshot_ref: stepScreenshotRefSchema
    .nullable()
    .optional()
    .describe(
      "When present, identifies the multipart binary part carrying this step's screenshot bytes.",
    ),
  url: z
    .string()
    .max(4096)
    .nullable()
    .optional()
    .describe(
      "Absolute URL of the page the walker was on. Required going forward; optional so older runs stay valid. Cloud stores walkthrough_steps.page_url and derives route_key.",
    ),
  surface: z
    .string()
    .max(200)
    .nullable()
    .optional()
    .describe(
      "Walker's short name for the screen (e.g. 'search results'). Stored as walkthrough_steps.surface.",
    ),
});
export type BundleStep = z.infer<typeof bundleStepSchema>;

/**
 * Inlined finding — full YAML frontmatter + markdown body. Findings ride
 * with the bundle (rather than being pushed separately) because local
 * finding files already carry `run: <run_id>` in frontmatter — the
 * walkthrough push is the natural cut-point to ingest them atomically.
 *
 * The cloud handler validates each against `findingFrontmatterSchema`
 * — same shape the `/import` endpoint expects for standalone finding
 * entities — and inserts with `walkthrough_id: <run_id>`.
 */
export const bundleFindingSchema = z.object({
  slug: z.string().min(1).max(512),
  frontmatter: findingFrontmatterSchema,
  content_hash: z
    .string()
    .max(128)
    .optional()
    .describe(
      "The finding file's content hash as the CLI records it for /import. Stored as the row's version token, so the CLI's later /import of the same finding (base_hash = this) is not a conflict. Declared by the `bundle-finding-hash` capability.",
    ),
  body: z
    .string()
    .max(1_048_576)
    .describe(
      "Full markdown body of the finding file. 1 MiB cap matches the entity import envelope.",
    ),
  screenshot_refs: z
    .array(stepScreenshotRefSchema)
    .max(32)
    .optional()
    .describe(
      "Optional per-finding screenshot parts (distinct from step screenshots — e.g. annotated evidence shots attached to the finding write-up).",
    ),
});
export type BundleFinding = z.infer<typeof bundleFindingSchema>;

/**
 * Walkthrough manifest — the one-row walkthrough state.
 *
 * Mirrors the subset of cloud `walkthroughs` columns that local runs
 * populate. Cloud-only columns (`prototype_version_id`, `runner_id`,
 * `batch_id`, etc.) aren't represented — local runs don't produce them
 * and the cloud handler leaves them NULL on insert.
 */
const verificationItemSchema = z.object({
  canonical_id: z.string().max(64).optional(),
  id: z.string().max(64).optional(),
  title: z.string().max(1024),
  type: z.string().max(32).optional(),
});
const verificationGroup = z.array(verificationItemSchema).max(500);

/** Since-last-run delta, shape of canonical.json `verification`. */
export const bundleVerificationSchema = z.object({
  baseline_run_id: z.string().max(256),
  resolved: verificationGroup.default([]),
  persists: verificationGroup.default([]),
  not_observed: verificationGroup.default([]),
  uncertain: verificationGroup.default([]),
  new_findings: verificationGroup.default([]),
});

const canonicalIdList = z.array(z.string().max(64)).max(1000).default([]);
const screenList = z.array(z.string().max(2048)).max(500).default([]);

/**
 * `designer_review.verification` — the designer's outcomes for earlier
 * standalone designer canonicals (`canonical.json` `verification.designer`).
 * Every list holds canonical ids:
 *   - `resolved`: on a judged screen, and no finding of this pass carries it;
 *   - `persists`: a finding of this pass carries it;
 *   - `not_observed`: its screen was not judged;
 *   - `uncertain`: an inline pass (`dispatch: inline`) records every
 *     outcome here.
 * `judged_screens` names the screens the pass looked at.
 */
export const designerReviewVerificationSchema = z.object({
  judged_screens: screenList,
  resolved: canonicalIdList,
  persists: canonicalIdList,
  not_observed: canonicalIdList,
  uncertain: canonicalIdList,
});
export type DesignerReviewVerification = z.infer<
  typeof designerReviewVerificationSchema
>;

/**
 * The run's designer pass record (`designer.json`, falling back to
 * `ux.json`), carried in the manifest and stored on
 * `walkthroughs.designer_review`. Mirrors `designerReviewSchema` in
 * `@iamu-dev/protocol`, read leniently: unknown keys are kept, so a newer
 * CLI's extra fields survive the round trip.
 */
export const designerReviewSchema = z
  .object({
    /** Who judged: `designer@<version>`. */
    judge: z.string().max(200).nullish(),
    /** `inline` when the orchestrator wrote the review; its outcomes are uncertain. */
    dispatch: z.enum(["subagent", "inline"]).nullish(),
    /** What it was judged against: the design docs, or general craft. */
    judged_against: z.enum(["design-docs", "general-craft"]).nullish(),
    design_files: z.array(z.string().max(1024)).max(200).default([]),
    summary: z.string().max(8192).default(""),
    /** Set when the pass was skipped: the reason (e.g. `--no-ux`). Nothing else counts then. */
    skipped: z.string().max(1024).nullish(),
    /** Design-doc rules, held or broken, with where they were judged. */
    rules: z
      .array(
        z
          .object({
            rule: z.string().max(1024),
            file: z.string().max(1024).nullish(),
            holds: z.boolean(),
            screen: z.string().max(2048).nullish(),
            steps: z.array(z.number().int()).default([]),
          })
          .passthrough(),
      )
      .max(500)
      .default([]),
    verification: designerReviewVerificationSchema.nullish(),
  })
  .passthrough();
export type DesignerReview = z.infer<typeof designerReviewSchema>;

const shaSchema = z.string().regex(/^[0-9a-f]{7,64}$/i);

const verdictRatingSchema = z.enum(["yes", "no", "partial", "not_applicable"]);

/**
 * One judge verdict. The CLI accepts camelCase (`humanTestId`, `rating`)
 * and the `verdicts.json` snake_case spelling (`human_test_id`,
 * `verdict`); both are normalised to the camelCase shape the cloud
 * stores, so a spelling never decides whether a verdict survives.
 */
export const bundleVerdictItemSchema = z
  .object({
    humanTestId: z.string().optional(),
    human_test_id: z.string().optional(),
    question: z.string().default(""),
    rating: verdictRatingSchema.optional(),
    verdict: verdictRatingSchema.optional(),
    reasoning: z.string(),
    groundingStepIds: z.array(z.string()).optional(),
    grounding_step_ids: z.array(z.string()).optional(),
    groundingFindingIds: z.array(z.string()).optional(),
    grounding_finding_ids: z.array(z.string()).optional(),
  })
  .refine(
    (v) =>
      Boolean(v.humanTestId || v.human_test_id) &&
      Boolean(v.rating || v.verdict),
    {
      message:
        "verdict item must include humanTestId or human_test_id, and rating or verdict",
    },
  )
  .transform((v) => ({
    humanTestId: (v.humanTestId || v.human_test_id) as string,
    question: v.question,
    rating: (v.rating ?? v.verdict) as z.infer<typeof verdictRatingSchema>,
    reasoning: v.reasoning,
    groundingStepIds: v.groundingStepIds ?? v.grounding_step_ids ?? [],
    groundingFindingIds: v.groundingFindingIds ?? v.grounding_finding_ids ?? [],
  }));

/**
 * Build the run was taken against (CONTRACT §6). The CLI auto-detects
 * the commit / branch and CI variables; everything is optional. Stored
 * raw on walkthroughs.build (the quality gate normalises it later).
 */
export const bundleBuildSchema = z.object({
  label: z.string().max(200).optional(),
  git_sha: shaSchema.optional(),
  git_branch: z.string().max(255).optional(),
  base_sha: shaSchema.optional(),
  ci: z
    .object({
      provider: z.string().max(64),
      run_url: z.string().url().max(2048),
    })
    .optional(),
  /**
   * The working tree had changes outside `iamu/` (tracked vs HEAD, or
   * untracked and not ignored). Such a run tests a build no commit names.
   */
  git_dirty: z.boolean().optional(),
  /**
   * Content hash of the dirty working tree (12 lowercase hex), only when
   * `git_dirty`. Identical working trees give identical hashes, so each
   * distinct uncommitted state becomes its own build. Accepted leniently
   * (6–64 hex, any case) so a format drift never fails the whole push.
   */
  worktree_hash: z
    .string()
    .regex(/^[0-9a-f]{6,64}$/i)
    .optional(),
});
export type BundleBuild = z.infer<typeof bundleBuildSchema>;

export const bundleManifestSchema = z.object({
  run_id: z
    .string()
    .uuid()
    .describe(
      "Client-minted UUID. Becomes `walkthroughs.id` on cloud. Idempotent key for the push endpoint.",
    ),
  scenario_id: z
    .string()
    .min(1)
    .describe(
      "Scenario reference — slug in local-only mode, UUID in cloud-linked mode. Cloud handler resolves to the tenant's scenarios.id.",
    ),
  target_url: z.string().url().max(2048),
  lenses: z
    .array(z.string().min(1).max(64))
    .min(1)
    .max(16)
    .describe(
      "Analysis lenses enabled for this run. Defaults to ['persona'] in most flows.",
    ),
  status: z
    .literal("completed")
    .describe(
      "v1 only accepts completed bundles (bundle-on-complete). In-progress runs are not pushable.",
    ),
  client_origin: z
    .literal("local")
    .describe(
      "Discriminator written to walkthroughs.client_origin. Reserved for `local` in this endpoint; runner-origin rows take a different code path.",
    ),
  created_at: isoTimestamp,
  completed_at: isoTimestamp,
  step_count: z.number().int().min(0).max(10_000),
  finding_count: z.number().int().min(0).max(10_000),
  report_markdown: z
    .string()
    .max(1_048_576)
    .describe(
      "report.md body verbatim. Persists to walkthroughs.report_markdown so dashboard can render without regenerating.",
    ),
  build: bundleBuildSchema
    .optional()
    .describe(
      "Build metadata (label, commit, branch, CI run). Absent from older CLIs.",
    ),
  environment: z
    .string()
    .max(256)
    .optional()
    .describe(
      "Environment the run targeted — a normalised origin or an explicit name.",
    ),
  what_worked: z.array(z.string().max(4096)).max(64).optional(),
  open_questions: z.array(z.string().max(4096)).max(64).optional(),
  verification: bundleVerificationSchema
    .optional()
    .describe(
      "Since-last-run delta from canonical.json — which earlier findings persist, were re-checked fixed, were not seen, and which are new. Persists to walkthroughs.verification.",
    ),
  blocked: z
    .object({
      reason: z.string().min(1).max(2048),
    })
    .optional()
    .describe(
      "Set when the walk could not start as set up (missing preconditions).",
    ),
  designer_review: designerReviewSchema
    .nullish()
    .describe(
      "The run's designer pass record (designer.json). Persists to walkthroughs.designer_review; its verification moves standalone designer canonicals.",
    ),
  verdict: z.array(bundleVerdictItemSchema).optional(),
  judge_meta: z
    .object({
      overall: z.object({
        question: z.string(),
        answer: z.string(),
        reasoning: z.string(),
      }),
      provenance: z
        .object({
          judge_prompt: z.string().optional(),
          judge_model: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  models: z
    .object({
      walker: z.string().max(256).optional(),
      matcher: z.string().max(256).optional(),
      judge: z.string().max(256).optional(),
      walker_reported: z.string().max(256).optional(),
      matcher_reported: z.string().max(256).optional(),
      judge_reported: z.string().max(256).optional(),
      walker_tier: z.string().max(64).optional(),
      matcher_tier: z.string().max(64).optional(),
      judge_tier: z.string().max(64).optional(),
      execution_mode: z.enum(["multi_agent", "single_agent"]).optional(),
      velocity_sec_per_step: z.number().nonnegative().optional(),
      total_duration_sec: z.number().nonnegative().optional(),
    })
    .optional()
    .describe(
      "AI model tier configuration, runtime provenance and turn velocity metrics.",
    ),
});
export type BundleManifest = z.infer<typeof bundleManifestSchema>;

/**
 * Top-level bundle envelope. The JSON `manifest` multipart field
 * carries a stringified version of this object.
 */
export const walkthroughBundleSchema = z
  .object({
    manifest: bundleManifestSchema,
    steps: z.array(bundleStepSchema).max(10_000),
    findings: z.array(bundleFindingSchema).max(10_000),
    artifacts: z.array(artifactRefSchema).max(10_000),
  })
  .superRefine((bundle, ctx) => {
    // Sanity checks the cloud handler relies on to stream binaries
    // without re-scanning everything. Catching them in the schema means
    // the handler's happy path is strictly "everything validated, now
    // just copy bytes."

    // 1. step_count matches steps.length.
    if (bundle.manifest.step_count !== bundle.steps.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["manifest", "step_count"],
        message: `manifest.step_count (${bundle.manifest.step_count}) must equal steps.length (${bundle.steps.length})`,
      });
    }

    // 2. finding_count matches findings.length.
    if (bundle.manifest.finding_count !== bundle.findings.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["manifest", "finding_count"],
        message: `manifest.finding_count (${bundle.manifest.finding_count}) must equal findings.length (${bundle.findings.length})`,
      });
    }

    // 3. step seqs are unique + dense 1..N. Dense sequences let the
    //    cloud handler loop 1..step_count without a join. Gaps or
    //    duplicates signal a broken recording path; fail early.
    const seqs = bundle.steps.map((s) => s.seq).sort((a, b) => a - b);
    const expected = Array.from(
      { length: bundle.steps.length },
      (_, i) => i + 1,
    );
    if (seqs.join(",") !== expected.join(",")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["steps"],
        message: `step seq values must be dense 1..${bundle.steps.length}; received [${seqs.join(",")}]`,
      });
    }

    // 3b. Screenshots without a single step mean the client failed to
    //     read its step records. Accepting the bundle would store an
    //     empty walkthrough that the run_id idempotency key then makes
    //     permanent.
    if (bundle.steps.length === 0 && bundle.artifacts.length > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["steps"],
        message: `bundle declares ${bundle.artifacts.length} artifact(s) but no steps — step records were not read`,
      });
    }

    // 4. Artifact part_names are unique within the bundle. The
    //    multipart body keys on part_name; duplicates would collide at
    //    decode time with unpredictable winner.
    const partNames = bundle.artifacts.map((a) => a.part_name);
    const seen = new Set<string>();
    for (const name of partNames) {
      if (seen.has(name)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["artifacts"],
          message: `duplicate artifact part_name: ${name}`,
        });
        break;
      }
      seen.add(name);
    }

    // 5. Every step.screenshot_ref.part_name + every finding
    //    screenshot_refs[].part_name MUST appear in artifacts[]. The
    //    cloud handler walks artifacts[]; orphan refs would read a
    //    binary that isn't in the multipart body (404 mid-ingest).
    const artifactPartNames = new Set(partNames);
    for (const [i, step] of bundle.steps.entries()) {
      if (
        step.screenshot_ref &&
        !artifactPartNames.has(step.screenshot_ref.part_name)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["steps", i, "screenshot_ref", "part_name"],
          message: `step[${step.seq}].screenshot_ref.part_name "${step.screenshot_ref.part_name}" is not declared in artifacts[]`,
        });
      }
    }
    for (const [i, finding] of bundle.findings.entries()) {
      for (const [j, ref] of (finding.screenshot_refs ?? []).entries()) {
        if (!artifactPartNames.has(ref.part_name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["findings", i, "screenshot_refs", j, "part_name"],
            message: `finding[${finding.slug}].screenshot_refs[${j}].part_name "${ref.part_name}" is not declared in artifacts[]`,
          });
        }
      }
    }

    // 6. Findings reference the manifest's scenario. Mismatch means
    //    the finding belongs to a different walkthrough than the one
    //    being pushed — ingest would insert rows under the wrong
    //    parent. Fail the whole bundle.
    for (const [i, finding] of bundle.findings.entries()) {
      if (finding.frontmatter.scenario !== bundle.manifest.scenario_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["findings", i, "frontmatter", "scenario"],
          message: `finding.frontmatter.scenario "${finding.frontmatter.scenario}" must match manifest.scenario_id "${bundle.manifest.scenario_id}"`,
        });
      }
    }

    // 7. Findings' run ref matches the manifest's run_id. Same class
    //    of bug as 6 but for the walkthrough-FK side.
    for (const [i, finding] of bundle.findings.entries()) {
      if (finding.frontmatter.run !== bundle.manifest.run_id) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["findings", i, "frontmatter", "run"],
          message: `finding.frontmatter.run "${finding.frontmatter.run}" must match manifest.run_id "${bundle.manifest.run_id}"`,
        });
      }
    }
  });
export type WalkthroughBundle = z.infer<typeof walkthroughBundleSchema>;

/**
 * What a successful push did to the cloud copy of the run.
 *
 * - `created`: full ingest (new run, or replacement of a run that never
 *   completed on cloud).
 * - `updated`: the run was already completed; only its mutable parts
 *   (report, verdicts, judge meta, new inlined findings) were written.
 *   Steps and stored screenshots are immutable and left untouched.
 * - `unchanged`: the run was already completed and nothing mutable
 *   differed; no writes.
 */
export const WALKTHROUGH_PUSH_OUTCOMES = [
  "created",
  "updated",
  "unchanged",
] as const;
export type WalkthroughPushOutcome = (typeof WALKTHROUGH_PUSH_OUTCOMES)[number];

/**
 * 200 body of `POST /api/projects/:id/walkthroughs/push`.
 *
 * @example
 * ```ts
 * const body = walkthroughPushResponseSchema.parse(await res.json());
 * if (body.status === "updated") console.log("verdicts refreshed");
 * ```
 */
export const walkthroughPushResponseSchema = z.object({
  run_id: z.string().uuid(),
  cloud_walkthrough_id: z.string().uuid(),
  status: z.enum(WALKTHROUGH_PUSH_OUTCOMES),
  step_count: z.number().int().min(0),
  finding_count: z.number().int().min(0),
  artifact_count: z.number().int().min(0),
});
export type WalkthroughPushResponse = z.infer<
  typeof walkthroughPushResponseSchema
>;

/**
 * Why the cloud refused a push of an already-completed run. Each one
 * means the bundle describes a different run than the one stored under
 * the same `run_id`, so retrying the same bundle can never succeed.
 */
export const WALKTHROUGH_CONFLICT_REASONS = [
  "project_mismatch",
  "scenario_mismatch",
  "step_count_mismatch",
] as const;
export type WalkthroughConflictReason =
  (typeof WALKTHROUGH_CONFLICT_REASONS)[number];

/**
 * 409 body of `POST /api/projects/:id/walkthroughs/push`. Terminal:
 * clients must not record the push as done and must not retry it.
 *
 * @example
 * ```ts
 * const conflict = walkthroughPushConflictSchema.safeParse(body);
 * if (conflict.success) console.error(conflict.data.reason);
 * ```
 */
export const walkthroughPushConflictSchema = z.object({
  code: z.literal("walkthrough_conflict"),
  reason: z.enum(WALKTHROUGH_CONFLICT_REASONS),
  run_id: z.string().uuid(),
  message: z.string(),
});
export type WalkthroughPushConflict = z.infer<
  typeof walkthroughPushConflictSchema
>;
